import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter as Router, Route } from 'react-router-dom'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import { withLDProvider } from 'launchdarkly-react-client-sdk'
import {
  QueryParamProvider,
  transformSearchStringJsonSafe,
} from 'use-query-params'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'


import Routes from './routes'
import { ThemeProvider as ClientThemeProvider, useTheme } from '../utils/themeContext'
import ClientIDProvider from '../utils/clientContext'
import { Toaster } from 'react-hot-toast'

const muiCache = createCache({
  key: 'mui',
  prepend: true,
})


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 10 * 1000 // Time in milliseconds
    }
  }
})
const queryStringifyOptions = {
  transformSearchString: transformSearchStringJsonSafe,
}

const ThemedApp = () => {
  const { theme } = useTheme()

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Toaster position="top-center" />
      <Routes />
      <ReactQueryDevtools />
    </ThemeProvider>
  )
}

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <QueryParamProvider
        ReactRouterRoute={Route}
        stringifyOptions={queryStringifyOptions}
      >
        <CacheProvider value={muiCache}>
          <ClientIDProvider>
            <ClientThemeProvider>
              <ThemedApp />
            </ClientThemeProvider>
          </ClientIDProvider>
        </CacheProvider>
      </QueryParamProvider>
    </QueryClientProvider>
  )
}

const LDApp = withLDProvider({
  clientSideID: process.env.LD_KEY,
  deferInitialization: false,
})(App)

const rootElement = document.getElementById('root')
const root = createRoot(rootElement)
root.render(
  <Router>
    <LDApp />
  </Router>)

